package fr.frux.splash

import androidx.compose.animation.core.EaseInOutCubic
import androidx.compose.animation.core.EaseOutBack
import androidx.compose.animation.core.EaseOutCubic
import androidx.compose.animation.core.Easing
import androidx.compose.animation.core.LinearEasing
import kotlin.math.PI
import kotlin.math.sin

/** Une frame de l'animation ; offsets exprimés dans l'espace auteur (logo composé sur 720). */
data class SplashFrame(
    val pinAlpha: Float,
    val pinOffsetY: Float,
    val pinSquash: Float,
    val crownScale: Float,
    val branchScale: Float,
    val branchAlpha: Float,
    val leafScale: Float,
    val leafAlpha: Float,
    val fruitScales: List<Float>,
    val settleOffsetY: Float,
    val nameAlpha: Float,
    val nameOffsetY: Float,
    val tagAlpha: Float,
    val tagOffsetY: Float,
)

/**
 * Timings du design Claude (splash.jsx) compressés de 5,2 s à 2,4 s.
 * Fonction pure de t : rejouable, testable, indépendante de l'horloge murale.
 */
object SplashTimeline {
    const val TOTAL: Float = 2.4f
    private const val FRUIT_COUNT: Int = 5

    private fun ramp(
        t: Float,
        start: Float,
        dur: Float,
        ease: Easing,
    ): Float {
        val x = ((t - start) / dur).coerceIn(0f, 1f)
        return when {
            x < 1e-4f -> 0f
            x > 1f - 1e-4f -> 1f
            else -> ease.transform(x)
        }
    }

    fun at(rawT: Float): SplashFrame {
        val t = rawT.coerceIn(0f, TOTAL)

        // Chute : le pin tombe (easeOutBack = rebond), s'écrase à l'impact
        val pinAlpha = ramp(t, 0.10f, 0.10f, LinearEasing)
        val pinOffsetY = -720f + 720f * ramp(t, 0.10f, 0.40f, EaseOutBack)
        val impact = ((t - 0.42f) / 0.18f).coerceIn(0f, 1f)
        val pinSquash = 1f - 0.13f * sin(PI.toFloat() * impact)

        // Pousse : couronne, puis branches, puis feuilles
        val crownScale = ramp(t, 0.55f, 0.50f, EaseOutBack)
        val branchP = ramp(t, 0.80f, 0.35f, EaseOutBack)
        val leafP = ramp(t, 0.95f, 0.25f, EaseOutBack)

        // Fruits : pop décalé
        val fruitScales =
            List(FRUIT_COUNT) { i ->
                ramp(t, 1.15f + i * 0.07f, 0.28f, EaseOutBack).coerceAtLeast(0f)
            }

        // Marque : le logo remonte, le nom puis la signature entrent
        val settle = -110f * ramp(t, 1.55f, 0.45f, EaseInOutCubic)
        val nameP = ramp(t, 1.70f, 0.35f, EaseOutCubic)
        val tagP = ramp(t, 1.90f, 0.35f, EaseOutCubic)

        return SplashFrame(
            pinAlpha = pinAlpha,
            pinOffsetY = pinOffsetY,
            pinSquash = pinSquash,
            crownScale = crownScale.coerceAtLeast(0f),
            branchScale = 0.75f + 0.25f * branchP,
            branchAlpha = (branchP * 1.4f).coerceIn(0f, 1f),
            leafScale = 0.6f + 0.4f * leafP,
            leafAlpha = (leafP * 1.4f).coerceIn(0f, 1f),
            fruitScales = fruitScales,
            settleOffsetY = settle,
            nameAlpha = nameP,
            nameOffsetY = 44f * (1f - nameP),
            tagAlpha = tagP,
            tagOffsetY = 44f * (1f - tagP),
        )
    }
}
